import { useSelector } from "react-redux";
import Panel from "../../../../Components/Panels/Panel/panel";
import {
  AddCurrentNodeType,
  AddDiagramNode,
  GetDiagramEdges,
  GetDiagramNodes,
  GetLoadingNodes,
  GetNodes,
  GetSelectedNodeType,
  SetDiagramEdges,
  SetDiagramNodes,
  SetSelectedNodeType,
} from "../../../../Redux/Reducers/redux-slice-workflow-designer";
import {
  KalikoNodeCallJobRule,
  KalikoNodeCallWorkflow,
  KalikoNodeGatewayBase,
  KalikoNodeGetMetadata,
  KalikoNodeSetMetadata,
  KalikoNodeStartEndEventBase,
  KalikoNodeTimer,
  KalikoNodeType,
} from "../../../../Core/Api/Types/kaliko";
import { Box, Card, CardContent, CircularProgress, Stack, Typography, useTheme } from "@mui/material";
import { useDispatch } from "react-redux";
import callActivitySvg from "./svg/call-activity.svg";
import endEventSvg from "./svg/end-event-none.svg";
import gateNoneSvg from "./svg/gateway-none.svg";
import gateOrSvg from "./svg/gateway-or.svg";
import gateParallelSvg from "./svg/gateway-parallel.svg";
import gateXorSvg from "./svg/gateway-xor.svg";
import startEventConditionSvg from "./svg/start-event-condition.svg";
import timerSvg from "./svg/start-event-timer.svg";
import taskSvg from "./svg/task.svg";
import dataInputSvg from "./svg/data-input.svg";
import dataOutputSvg from "./svg/data-output.svg";
import serviceSvg from "./svg/service-task.svg";
import deleteSvg from "./svg/delete.svg";
import changeStatusSvg from "./svg/status-change.svg";
import dragSvg from "./svg/draggable-svgrepo-com.svg";
import { useDnD } from "./DnDContext";
import { useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import { KalikoNodeProps } from "./kaliko-node";

interface NodePickerPanelProps {}

const NodePickerPanel: React.FC<NodePickerPanelProps> = ({}) => {
  const kalikoNodeTypes = useSelector(GetNodes);
  const loadingNodes = useSelector(GetLoadingNodes);
  return (
    <Panel sx={{ overflow: "auto", overflowX: "hidden" }} open={true} position="left">
      {loadingNodes ? (
        <Stack width={"100%"} height="256px" justifyContent={"center"} alignItems={"center"}>
          <CircularProgress />
        </Stack>
      ) : (
        <Stack spacing={1} width={"100%"} direction={"column"}>
          {kalikoNodeTypes?.map((x, i) => (
            <KalikoCard key={i} data={x} />
          ))}
        </Stack>
      )}
    </Panel>
  );
};
interface KalikoCardProps {
  data:
    | KalikoNodeCallJobRule
    | KalikoNodeCallWorkflow
    | KalikoNodeGatewayBase
    | KalikoNodeGetMetadata
    | KalikoNodeSetMetadata
    | KalikoNodeStartEndEventBase
    | KalikoNodeTimer;
}
const KalikoCard: React.FC<KalikoCardProps> = ({ data }) => {
  const theme = useTheme();
  const [_, setType] = useDnD();

  const onDragStart = (event, nodeType) => {
    setType && setType(nodeType);
    event.dataTransfer.effectAllowed = "move";
  };

  const dispatch = useDispatch();
  const { name, systemName, type, defaultInputParams, defaultOutputParams } = data;
  const kalikoNodeTypes = useSelector(GetNodes);
  const diagramNodes = useSelector(GetDiagramNodes);
  const diagramEdges = useSelector(GetDiagramEdges);
  const selectedNodeType = useSelector(GetSelectedNodeType);
  const selected = selectedNodeType === type;
  const handleSelectClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (selected) {
      dispatch(AddCurrentNodeType());
    } else {
      dispatch(SetSelectedNodeType(type));
      dispatch(SetDiagramEdges(diagramEdges.map((x) => ({ ...x, selected: false }))));
      dispatch(SetDiagramNodes(diagramNodes.map((x) => ({ ...x, selected: false }))));
    }
  };

  return (
    <div>
      <Box
        borderRadius={"12px"}
        width={"100%"}
        sx={{ backgroundColor: selected ? theme.palette.primary.alphaHover : "" }}
        border={selected ? `1px solid ${theme.palette.primary.main}` : "1px solid transparent"}
      >
        <Card
          onClick={handleSelectClick}
          sx={{
            backgroundColor: selected ? theme.palette.primary.alphaPressed : "",
            width: "100%",
            borderRadius: "12px",
            cursor: "pointer",
          }}
        >
          <CardContent sx={{ padding: "6px !important" }}>
            <Stack alignItems={"center"} spacing={2} width={"100%"} direction={"row"} justifyContent={"space-between"}>
              <Stack direction={"row"} spacing={2} alignItems={"center"}>
                <div
                  style={{ height: "100%", display: "flex", alignItems: "center", flexDirection: "row" }}
                  onDragStart={(event) => onDragStart(event, data.type)}
                >
                  <NodeTypeSvg type={type} />
                </div>
                <Typography
                  maxHeight={"24px"}
                  overflow={"hidden"}
                  textOverflow={"ellipsis"}
                  variant="h6"
                  component="span"
                >
                  {systemName}
                </Typography>
              </Stack>
              <Stack height={"100%"} direction={"row"} spacing={2} alignItems={"center"}>
                <div
                  style={{ display: "flex", justifyContent: "center" }}
                  onDragStart={(event) => onDragStart(event, data.type)}
                >
                  <img
                    src={dragSvg}
                    style={{
                      cursor: "grab",
                      filter:
                        theme.palette.mode === "dark"
                          ? "invert(42%) sepia(0%) saturate(1352%) hue-rotate(87deg) brightness(249%) contrast(119%)"
                          : "brightness(0%)",
                      color: "white",
                      width: "24px",
                      height: "24px",
                      minHeight: "24px",
                      minWidth: "24px",
                    }}
                  />
                </div>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </div>
  );
};

interface NodeTypeIconProps {
  type: KalikoNodeType;
  context?: "flow" | "picker";
}
export const NodeTypeSvg: React.FC<NodeTypeIconProps> = ({ type, context = "picker" }) => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const imageStyle = {
    cursor: "grab",
    filter:
      mode === "dark"
        ? "invert(42%) sepia(0%) saturate(1352%) hue-rotate(87deg) brightness(249%) contrast(119%)"
        : "brightness(0%)",
    color: "white",
    width: context === "picker" ? "24px" : "36px",
    height: context === "picker" ? "24px" : "36px",
    minWidth: context === "picker" ? "24px" : "36px",
    minHeight: context === "picker" ? "24px" : "36px",
  };

  const imageSource = useMemo(() => {
    switch (type) {
      case KalikoNodeType.CallJobRule:
        return callActivitySvg;
      case KalikoNodeType.CallWorkflow:
        return serviceSvg;
      case KalikoNodeType.EndEvent:
        return endEventSvg;
      case KalikoNodeType.GetMetadata:
        return dataOutputSvg;
      case KalikoNodeType.SetMetadata:
        return dataInputSvg;
      case KalikoNodeType.StartEvent:
        return startEventConditionSvg;
      case KalikoNodeType.Timer:
        return timerSvg;
      case KalikoNodeType.UserTask:
        return taskSvg;
      case KalikoNodeType.GatewayParallel:
        return gateParallelSvg;
      case KalikoNodeType.GatewayExclusive:
        return gateXorSvg;
      case KalikoNodeType.GatewayInclusive:
        return gateOrSvg;
      case KalikoNodeType.ChangeStatus:
        return changeStatusSvg;
      case KalikoNodeType.FSDeleteFile:
        return deleteSvg;
      default:
        return gateNoneSvg;
    }
  }, [type]);
  return <img style={imageStyle} src={imageSource} />;
};
export default NodePickerPanel;
